import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import { Skeleton } from '@/components/shadcn/skeleton';
import badmintonService, { type BadmintonGameDetail } from '@/services/badmintonService';
import BadmintonSubpageHeader from '@/components/community/badminton/BadmintonSubpageHeader';
import BadmintonTimerPanel from '@/components/community/badminton/BadmintonTimerPanel';
import BadmintonScorePad from '@/components/community/badminton/BadmintonScorePad';
import {
  applyScoreValue,
  applySideDelta,
  buildScorePayload,
  buildSetsFromGame,
  canFillSet,
  countSetWins,
  scoringCap,
  scoringTarget,
  type SetScoreInput,
} from '@/components/community/badminton/badmintonScoringUtils';
import { displayTimerMs, formatTimerMmSs } from '@/components/community/badminton/badmintonTimerUtils';
import { formatCourtLabel, formatPlayerFormat } from '@/components/community/badminton/badmintonUiUtils';
import { hostPaths } from '@/utils/hostPaths';
import { clientPaths, isCustomerAppPath } from '@/utils/clientPaths';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { historyCanGoBack } from '@/utils/nativeNavigation';

export default function BadmintonEditSkorPage() {
  const { slug, gameSlug } = useParams<{ slug: string; gameSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isClient = isCustomerAppPath(location.pathname);
  const gameNumber = (location.state as { gameNumber?: number } | null)?.gameNumber ?? 1;
  const [game, setGame] = useState<BadmintonGameDetail | null>(null);
  const [sets, setSets] = useState<SetScoreInput[]>([]);
  const [activeSet, setActiveSet] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [receivedAt, setReceivedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const backPath =
    isClient && slug
      ? clientPaths.event.badminton(slug)
      : slug
        ? hostPaths.mabar.badminton(slug)
        : hostPaths.mabar.list;
  const goBack = () => {
    if (historyCanGoBack()) {
      navigate(-1);
      return;
    }
    navigate(backPath);
  };

  useEffect(() => {
    if (!gameSlug) return;
    badmintonService
      .getGame(gameSlug)
      .then((detail) => {
        setGame(detail);
        setSets(buildSetsFromGame(detail.maxSets, detail.setScores ?? []));
        setActiveSet(detail.timerSetNumber || 1);
        setReceivedAt(Date.now());
      })
      .catch((err: unknown) => setErrorMessage(getApiErrorMessage(err, 'Gagal memuat game')))
      .finally(() => setLoading(false));
  }, [gameSlug]);

  useEffect(() => {
    if (game?.timerStatus !== 'running') return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [game?.timerStatus]);

  const scoringType = game?.scoringType || 'rally_21';
  const current = sets.find((s) => s.setNumber === activeSet) || {
    setNumber: activeSet,
    scoreSideA: 0,
    scoreSideB: 0,
  };
  const { winsA, winsB } = countSetWins(sets, scoringType);
  const running = game?.timerStatus === 'running' && game.timerSetNumber === activeSet;

  const bump = (side: 'a' | 'b', delta: number) => {
    if (!game) return;
    if (delta > 0 && !canFillSet(activeSet, sets, game.maxSets, scoringType)) return;
    const next = applySideDelta(current.scoreSideA, current.scoreSideB, side, delta, scoringCap(scoringType));
    setSets((prev) => prev.map((s) => (s.setNumber === activeSet ? { ...s, ...next } : s)));
  };

  const setAbsoluteScore = (side: 'a' | 'b', value: number) => {
    if (!game) return;
    if (!canFillSet(activeSet, sets, game.maxSets, scoringType)) return;
    const next = applyScoreValue(
      current.scoreSideA,
      current.scoreSideB,
      side,
      value,
      scoringCap(scoringType)
    );
    setSets((prev) => prev.map((s) => (s.setNumber === activeSet ? { ...s, ...next } : s)));
  };

  const patchTimer = async (action: 'start' | 'pause' | 'reset') => {
    if (!gameSlug) return;
    try {
      const detail = await badmintonService.updateTimer(gameSlug, { action, setNumber: activeSet });
      setGame(detail);
      setReceivedAt(Date.now());
    } catch (err: unknown) {
      setErrorMessage(getApiErrorMessage(err, 'Gagal memperbarui timer'));
    }
  };

  const handleSave = async () => {
    if (!gameSlug || !slug) return;
    setSaving(true);
    try {
      await badmintonService.updateScores(gameSlug, buildScorePayload(sets, scoringType));
      navigate(backPath, {
        state: {
          feedback: {
            icon: 'mdi:check-circle-outline',
            title: 'Skor Disimpan',
            message: `Game ${gameNumber} · ${sets
              .filter((s) => s.scoreSideA > 0 || s.scoreSideB > 0)
              .map((s) => `${s.scoreSideA}-${s.scoreSideB}`)
              .join(', ')}. Klasemen dan statistik pemain ikut diperbarui.`,
          },
        },
      });
    } catch (err: unknown) {
      setErrorMessage(getApiErrorMessage(err, 'Gagal menyimpan skor'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-4 py-6"><Skeleton className="h-40 w-full rounded-2xl" /></div>;
  if (!game) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-gray-600">{errorMessage || 'Game tidak ditemukan'}</p>
        <Button className="mt-4" onClick={goBack}>Kembali</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 pb-28">
      <BadmintonSubpageHeader
        title={`Edit Skor · Game ${gameNumber}`}
        subtitle={[
          formatPlayerFormat(game.playerFormat),
          formatCourtLabel(game.courtNumber, game.sessionNumCourts),
          `${game.maxSets} set`,
          `target ${scoringTarget(scoringType)} poin`,
        ]
          .filter(Boolean)
          .join(' · ')}
        onBack={goBack}
      />
      <Card className="flex flex-row items-center justify-between rounded-2xl border-0 bg-white px-4 py-3 shadow-[inset_0_0_0_1px_#E0E6ED]">
        <p className="min-w-0 flex-1 text-center text-sm font-extrabold leading-tight text-[#0E1726]">
          {game.sideAPlayers.map((p) => p.playerName).join(' / ') || 'Sisi A'}
        </p>
        <Badge className="mx-2 shrink-0 border-0 bg-[#FFF1EC] px-2 py-1 text-xs font-bold text-[#FC480D] hover:bg-[#FFF1EC]">
          {winsA} - {winsB}
        </Badge>
        <p className="min-w-0 flex-1 text-center text-sm font-extrabold leading-tight text-[#0E1726]">
          {game.sideBPlayers.map((p) => p.playerName).join(' / ') || 'Sisi B'}
        </p>
      </Card>
      <div className="mt-3">
        <BadmintonTimerPanel
          display={formatTimerMmSs(displayTimerMs(game, activeSet, now, receivedAt))}
          running={Boolean(running)}
          onReset={() => void patchTimer('reset')}
        />
      </div>
      <BadmintonScorePad
        maxSets={game.maxSets}
        activeSet={activeSet}
        sets={sets}
        current={current}
        scoringType={scoringType}
        onSelectSet={setActiveSet}
        onBump={bump}
        onSetScore={setAbsoluteScore}
      />
      {errorMessage && <p className="mt-3 text-sm text-red-500">{errorMessage}</p>}
      <div className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-2 gap-2 bg-white p-4 pb-safe">
        <Button
          type="button"
          variant="secondary"
          onClick={() => void patchTimer(running ? 'pause' : 'start')}
          className="h-12 rounded-full bg-[#FFF1EC] text-sm font-bold text-[#FC480D] hover:bg-[#FFE4D8]"
        >
          <Icon icon={running ? 'mdi:pause' : 'mdi:play'} className="h-4 w-4" />
          {running ? 'Jeda Timer' : 'Mulai Timer'}
        </Button>
        <Button type="button" disabled={saving} onClick={() => void handleSave()} className="h-12 rounded-full bg-[#FC480D] font-bold text-white hover:bg-[#D83B08]">
          <Icon icon="mdi:check-circle-outline" className="h-4 w-4" />
          Simpan Skor
        </Button>
      </div>
    </div>
  );
}
